import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { commonMessages, generalMessages } from 'src/utils/messages';
import InputFormField from 'src/registry/components/forms/InputFormField';
import { Input } from 'src/registry/ui/Input';
import React from 'react';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { discountPoliciesMessages } from 'src/features/discountPolicies/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { BOOLEAN_MAP, BOOLEAN_OPTIONS, CURRENCY_MAP } from 'src/features/moneyAccounts/utils/constants';
import { VoucherTypeFormSchemaValues } from 'src/features/voucherTypes/utils/formSchema';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { VOUCHER_TYPE_USAGE, VoucherType } from 'src/features/voucherTypes/utils/types';
import { VOUCHER_USAGE_MAP } from 'src/features/voucherTypes/utils/constants';
import { CURRENCY } from 'src/types';
import TextareaFormField from 'src/registry/components/forms/TextareaFormField';
import { useListVouchers } from 'src/features/vouchers/api/useListVouchers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';

type VoucherTypeFormProps = {
    form: UseFormReturn<VoucherTypeFormSchemaValues>;
    onSubmit: (values: VoucherTypeFormSchemaValues) => void;
    selectedVoucherType?: VoucherType;
};

function VoucherTypeForm({ onSubmit, form, selectedVoucherType }: VoucherTypeFormProps) {
    const { formatMessage } = useIntl();
    const [displayAmount, setDisplayAmount] = React.useState('');

    const { amount, percent, valid_from } = form.watch();

    const nextDayFromValidFrom = new Date(valid_from);
    nextDayFromValidFrom.setDate(nextDayFromValidFrom.getDate() + 1);

    React.useEffect(() => {
        if (amount != null) {
            setDisplayAmount(String(amount / 100));
        } else {
            setDisplayAmount('');
        }
    }, [amount]);

    const isInEditMode = !!selectedVoucherType;

    // A value change never reaches vouchers that were already handed out — they snapshot their
    // name and value at issue time. Say so before it looks like a bug.
    const { data: issuedVouchers } = useListVouchers({
        voucher_type: selectedVoucherType?.id,
        limit: 1,
        config: { enabled: isInEditMode },
    });
    const issuedCount = issuedVouchers?.count ?? 0;

    const isAmountDisabled =
        (percent !== undefined && percent !== '') || (isInEditMode && selectedVoucherType?.percent !== null);
    const isPercentDisabled = amount !== undefined || (isInEditMode && selectedVoucherType?.amount !== null);

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            {issuedCount > 0 && (
                <div className="flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 px-4 py-3 text-sm text-orange-900">
                    <FontAwesomeIcon icon={ICON_MAP.CIRCLE_EXCLAMATION} className="mt-0.5 shrink-0" />
                    <span>{formatMessage(voucherTypesMessages.alreadyIssuedWarning, { count: issuedCount })}</span>
                </div>
            )}
            <InputFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="name"
                type="text"
                label={formatMessage(commonMessages.name)}
            />
            <InputFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="code"
                type="text"
                label={formatMessage(voucherTypesMessages.code)}
            />
            <TextareaFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="description"
                required={false}
                label={formatMessage(generalMessages.description)}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(generalMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: new Date() }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(generalMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime
                                    dateISO={field.value}
                                    onChange={field.onChange}
                                    disabledDateRange={{ before: nextDayFromValidFrom }}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <SelectFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="usage"
                required
                label={formatMessage(voucherTypesMessages.usage)}
                options={Object.values(VOUCHER_TYPE_USAGE)}
                optionTranslationMap={VOUCHER_USAGE_MAP}
            />
            <SelectFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                required
                label={formatMessage(voucherTypesMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
            <SelectFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
            />
            <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            <FormattedMessage {...discountPoliciesMessages.amount} />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder={`${formatMessage(discountPoliciesMessages.amount)}...`}
                                    value={displayAmount}
                                    onBlur={() => {
                                        if (!displayAmount) return;

                                        const numeric = parseFloat(displayAmount);
                                        if (!isNaN(numeric)) {
                                            setDisplayAmount(numeric.toFixed(2));
                                        }
                                    }}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        if (!/^\d*\.?\d{0,2}$/.test(inputValue)) return;

                                        setDisplayAmount(inputValue);

                                        const numeric = parseFloat(inputValue);
                                        if (!isNaN(numeric)) {
                                            field.onChange(Math.round(numeric * 100));
                                        } else {
                                            field.onChange(undefined);
                                        }
                                    }}
                                    disabled={isAmountDisabled}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <InputFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="percent"
                required={false}
                label={formatMessage(discountPoliciesMessages.percent)}
                pattern={/^-?\d{0,20}(?:\.\d{0,4})?$/}
                disabled={isPercentDisabled}
            />
            <InputFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="valid_duration"
                label={formatMessage(discountPoliciesMessages.validDuration)}
                type="text"
                required={false}
                pattern={/^\d+$/}
            />
            <InputFormField<VoucherTypeFormSchemaValues>
                form={form}
                name="max_count"
                type="number"
                required={false}
                label={formatMessage(voucherTypesMessages.maxCount)}
            />
        </Form>
    );
}

export default VoucherTypeForm;
